import React, { forwardRef, useRef } from "react";
import { parsePublicationDate } from "./publication-time";
import "./HexCallout.css";

const MARGIN = 12; // the timeline panel's side gutter (App.css)
const GAP = 3;     // between the hex ring and the pointer's tip
const POINTER = 7; // how far the pointer sticks out of the card
const POINTER_INSET = 18; // keeps the pointer clear of the rounded corners
const MIN_EXPANDED = 160; // less room than this above the hex and it opens below
const SWIPE = 40; // a swipe on the expanded header this far toward the hex closes it

// While the globe moves, each hovered hex's card waits, then fades up to a
// faint preview, so swiping across many hexes doesn't flash every card.
export const PREVIEW_DELAY_MS = 80;
export const PREVIEW_FADE_MS = 250;
export const PREVIEW_OPACITY = 0.75;
export const previewOpacity = (elapsedMs) => {
  const t = Math.min(1, Math.max(0, (elapsedMs - PREVIEW_DELAY_MS) / PREVIEW_FADE_MS));
  return PREVIEW_OPACITY * t * t * (3 - 2 * t);
};

// "2h ago"-style age, "" when the date is missing or unreadable.
export function shortAge(value, now = Date.now()) {
  if (value == null || value === "") return "";
  const date = parsePublicationDate(value);
  if (Number.isNaN(date.getTime())) return "";
  const minutes = Math.max(0, Math.round((now - date.getTime()) / 60000));
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// "3 stories near Kyiv": the most common location among the articles.
export function placeTitle(articles) {
  const counts = new Map();
  for (const a of articles) counts.set(a.location, (counts.get(a.location) || 0) + 1);
  let top = null;
  for (const [location, n] of counts) if (location && (!top || n > counts.get(top))) top = location;
  const n = articles.length;
  return `${n} ${n === 1 ? "story" : "stories"} near ${(top || "here").split(",")[0]}`;
}

const summary = (text) => {
  const words = text.split(" ");
  return words.slice(0, 20).join(" ") + (words.length > 20 ? "…" : "");
};

// Where the card goes for a hex ring centred at (x, y) with an on-screen
// radius `ring`, on a view `width` wide, between `top` (the category chips)
// and `bottom` (the timeline). Its pointer touches the ring either way.
//
// Collapsed, it's centred over the ring and kept inside the gutters, flipped
// below the ring when there's no room above, and `hidden` when it can't fit
// either way. Expanded, it spans the gutters and grows from the ring toward the
// menu button to fit its stories (`contentHeight`), scrolling past that; or
// from the ring down to the timeline when there's too little room above.
export function placeCallout({ x, y, ring, cardWidth, cardHeight, contentHeight = Infinity, width, top, bottom, expanded = false }) {
  const aboveEdge = y - ring - GAP - POINTER; // the card's bottom when above
  const belowEdge = y + ring + GAP + POINTER; // its top when below
  const offscreen = x < 0 || x > width;
  if (expanded) {
    const w = width - 2 * MARGIN;
    const roomAbove = aboveEdge - top;
    const roomBelow = bottom - belowEdge;
    const below = roomAbove < Math.min(contentHeight, MIN_EXPANDED) && roomBelow > roomAbove;
    const height = Math.max(0, Math.min(contentHeight, below ? roomBelow : roomAbove));
    const pointer = Math.min(Math.max(x - MARGIN, POINTER_INSET), w - POINTER_INSET);
    return { left: MARGIN, top: below ? belowEdge : aboveEdge - height, width: w, height, pointer, below, hidden: offscreen };
  }
  const left = Math.min(Math.max(x - cardWidth / 2, MARGIN), Math.max(MARGIN, width - MARGIN - cardWidth));
  const pointer = Math.min(Math.max(x - left, POINTER_INSET), cardWidth - POINTER_INSET);
  const below = aboveEdge - cardHeight < top;
  const cardTop = below ? belowEdge : aboveEdge - cardHeight;
  const hidden = offscreen || cardTop < top || cardTop + cardHeight > bottom;
  return { left, top: cardTop, pointer, below, hidden };
}

function Story({ item, onOutbound, onWatchMission }) {
  // A special story (artemis-mission.js) replays its event instead of linking out.
  if (item.special) {
    return (
      <div className="hex-callout__article hex-callout__special">
        <span className="hex-callout__text">
          <span className="hex-callout__title">{item.title}</span>
          {item.description && <span className="hex-callout__summary">{summary(item.description)}</span>}
          <span className="hex-callout__actions">
            <button type="button" className="mission-watch" onClick={() => onWatchMission?.(item.special)}>Watch the mission</button>
            <a href={item.link} target="_blank" rel="noreferrer" onClick={onOutbound}>NASA mission page</a>
          </span>
        </span>
      </div>
    );
  }
  return (
    <a className={`hex-callout__article${item.image ? " has-photo" : ""}`} href={item.link} target="_blank" rel="noreferrer" onClick={onOutbound}>
      <span className="hex-callout__text">
        <span className="hex-callout__title">{item.title}</span>
        {item.description && <span className="hex-callout__summary">{summary(item.description)}</span>}
        <span className="hex-callout__meta">{[item.source, shortAge(item.date)].filter(Boolean).join(" · ")}</span>
        {item.translatedFrom && <span className="hex-callout__meta">Machine-translated from {item.translatedFrom}</span>}
      </span>
      {item.image && <img className="hex-callout__photo" src={item.image} loading="lazy" alt="" />}
    </a>
  );
}

// The open hex's stories, pinned over its column (GlobeComponent positions
// and sizes it every frame through the forwarded ref). Collapsed it shows the
// lead story; tapping it expands to every story, with photos and summaries.
// `expanded` is the stories it expanded with (GlobeComponent holds them while
// the camera zooms in), or null.
const HexCallout = forwardRef(function HexCallout({ articles, expanded, onExpand, onCollapse, onOutbound, onWatchMission }, ref) {
  const stories = (expanded || articles).filter((a) => a.title);
  const lead = stories[0];

  // A swipe on the header toward the hex (down when the card sits above it,
  // up when below) closes the card.
  const swipeStart = useRef(null);
  const onHeaderDown = (e) => {
    if (e.target.closest("button")) return; // the close button taps as usual
    swipeStart.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId); // the swipe can end off the header
  };
  const onHeaderUp = (e) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const below = e.currentTarget.closest(".hex-callout")?.classList.contains("is-below");
    if (Math.abs(dy) > Math.abs(dx) && (below ? -dy : dy) > SWIPE) onCollapse();
  };

  return (
    <div
      ref={ref}
      className={`hex-callout ui-panel${expanded ? " is-expanded" : ""}`}
      aria-hidden={!lead}
      role={expanded ? "dialog" : undefined}
      aria-label={expanded ? placeTitle(expanded) : undefined}
    >
      {lead && !expanded && (
        <button type="button" className="hex-callout__lead" onClick={onExpand} aria-expanded="false">
          <span className="hex-callout__lead-text">
            <span className="hex-callout__title hex-callout__title--clamp">{lead.title}</span>
            <span className="hex-callout__meta">
              {[lead.source, shortAge(lead.date)].filter(Boolean).join(" · ")}
              {stories.length > 1 && ` · ${stories.length} stories`}
            </span>
          </span>
          {/* Points the way the card opens (flipped when it sits below). */}
          <svg className="hex-callout__chevron" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 15l6-6 6 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {lead && expanded && (
        <>
          <div
            className="hex-callout__header"
            onPointerDown={onHeaderDown}
            onPointerUp={onHeaderUp}
            onPointerCancel={() => { swipeStart.current = null; }}
          >
            <span className="hex-callout__handle" aria-hidden="true" />
            <span className="hex-callout__place">{placeTitle(expanded)}</span>
            <button type="button" className="hex-callout__close" onClick={onCollapse} aria-label="Collapse stories">×</button>
          </div>
          <div className="hex-callout__scroll">
            {stories.map((item, i) => <Story key={item.article_id || i} item={item} onOutbound={onOutbound} onWatchMission={onWatchMission} />)}
          </div>
        </>
      )}
    </div>
  );
});

export default HexCallout;
